import React, { useEffect, useState } from 'react';
import { Button, Text, View } from "../Components/Themed"
import type { AthleteProps, EventProps, ExternalPriceProps, LeagueProps, MarketProps, OrderProps, TeamProps, TournamentProps, TradeProps } from '../types';
import { MarketComponentApi, MarketComponentHelpers } from '../MarketComponents/api';
import { ActivityIndicator, Image } from 'react-native';
import moment from 'moment-mini';
import { useColors } from '../constants/useColors';
import { FINALIZED_STATUSES } from '../Competition/api';
import TradeText from './components/TradeText';
import SocketManager from '../Socket';
import { AthleteImage } from '../Components/Jerseys';
import { MarketButtonHelpers } from '../MarketComponents/components/MarketButton/api';
import { Icons } from '../Components';


type TickerProps = {
    league_ids:string[],
    color_mode?:'light'|'dark',
    distinct_id?:string,
    onOrders?:(orders:OrderProps[]) => void,
    onCreateOrder?:(order:OrderProps) => void
}

const MIN_SIZE = 365
const MARGIN = 6

const Ticker = ({ league_ids, color_mode, distinct_id, onOrders, onCreateOrder }:TickerProps) => {
    const Colors = useColors({ mode: color_mode });
    const [ embed_size, setEmbedSize ] = useState({ height:0, width:0 });
    const [ needs_reload, setNeedsReload ] = useState(false);
    const [ socket_event, setSocketEvent ] = useState<EventProps | undefined>(undefined);
    const [ socket_trade, setSocketTrade ] = useState<TradeProps[]>([])
    const [ ticker_data, setTickerData ] = useState<{
        loading:boolean,
        markets:MarketProps[],
        events:EventProps[],
        athletes:AthleteProps[],
        teams:TeamProps[],
        tournaments:TournamentProps[],
        leagues:LeagueProps[],
        trades:TradeProps[],
        animate:boolean,
        external_prices:ExternalPriceProps[],
    }>({
        loading:false,
        events: [],
        markets:[],
        animate: false,
        tournaments:[],
        athletes:[],
        teams: [],
        trades: [],
        leagues: [],
        external_prices: []
    });
    const { loading, trades, leagues, events, tournaments, athletes, teams, animate, markets } = ticker_data;
    let sorted_events = events.sort((a,b) => {
        return moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix() || parseInt(a.event_id) - parseInt(b.event_id)
    });
    let sorted_tournaments = tournaments.sort((a,b) => {
        return moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix() || parseInt(a.tournament_id) - parseInt(b.tournament_id)
    });

    const primary_markets = markets.filter(m => m.primary_market && m.event_type == 'team');

    const calcWidth = (width:number, min_size:number, margin:number) => {
        //Check how many we can do min size
        let number_to_render = Math.floor(width / min_size)
        let width_no_margin = width / number_to_render
        return { tick_width: width_no_margin - (margin*2), num_columns:number_to_render }
    }

    const { tick_width, num_columns } = calcWidth(embed_size.width, MIN_SIZE, MARGIN);
    let column_headers:{ number:number }[] = []
    for (let i = 0; i < num_columns; i++) {
        column_headers.push({number: i+1});
    }
    useEffect(() => {
        startUp();
    },[]);

    useEffect(() => {
        if(!socket_event){ return }
        let exists = events.find(e => e.event_id == socket_event.event_id)
        if(!exists){ return }
        setTickerData({
            ...ticker_data,
            events: events.filter(e => e.event_id != socket_event.event_id).concat(socket_event)
        })
        setSocketEvent(undefined)
    },[socket_event])

    useEffect(() => {
        if(socket_trade.length == 0){ return }
        let market = markets.find(m => m.market_id == socket_trade[0]?.market_id)
        if(!market){ return }//

        let nt = socket_trade.find(t => t.side == market.trade_side && t.market_type == 'FOR_MONEY');
        if(!nt){ return }
        let matching_trade = trades.find(t => t.event_type == nt.event_type && t.event_id == nt.event_id && t.market_id == nt.market_id && t.side == nt.side && t.side_id == nt.side_id);
        setTickerData({
            ...ticker_data,
            trades: trades.filter(t => t.trade_id != matching_trade?.trade_id).concat(nt),
            animate: true,
        })
        setSocketTrade([]);
    },[socket_trade])

    const startUp = async() => {
        MarketComponentApi.setEnvironment();
        await getData()
    }

    const getData = async() => {
        setTickerData({ ...ticker_data, loading: true })
        const lgs = await MarketComponentApi.getLeagues();
        const mks = await MarketComponentApi.getMarkets();
        let evs = await MarketComponentApi.getActiveEvents();
        let tns = await MarketComponentApi.getActiveTournaments();
        if(league_ids.length > 0){
            evs = evs.filter(e => league_ids.map(id => id.toString()).includes(e.league_id.toString()));
            tns = tns.filter(t => league_ids.map(id => id.toString()).includes(t.league_id.toString()));
        }
        let ath_ids:string[] = []
        let t_ids:string[] = []
        const lts = await MarketComponentApi.getLatestTradesByEvents('team', evs.map(e => e.event_id));

        tns.map(t => {
            if(t.participant_type == 'athlete'){ ath_ids = ath_ids.concat(t.participants) }
            if(t.participant_type == 'team'){ t_ids = t_ids.concat(t.participants) }
        })
        const aths = await MarketComponentApi.getAthletesByIds(ath_ids);
        const tms = await MarketComponentApi.getTeamsByIds(t_ids);
        const tn_ts = await MarketComponentApi.getLatestTradesByEvents('tournament', tns.map(t => t.tournament_id));
        setTickerData({
            ...ticker_data,
            events: evs,
            loading:false,
            animate:false,
            athletes: aths,
            teams: tms,
            tournaments: tns,
            markets: mks,
            trades: lts.concat(tn_ts).filter(t => t.market_type == 'FOR_MONEY'),
            leagues: lgs
        });
    }


    const renderTrades = (data: { item:MarketProps, index:number, contest_type:string, contest_id:string, is_live:boolean, side_type?:string, side_id?:string }) => {
        let ev_trades = trades.filter(t => t.event_type == data.contest_type && t.event_id == data.contest_id && t.market_id == data.item.market_id);
        if(data.side_type){ ev_trades = ev_trades.filter(t => t.side_type == data.side_type && t.side_id == data.side_id) }
        const trade_side_trade = ev_trades.find(t => t.side == data.item.trade_side);
        //if(!trade_side_trade){ return <></> }
        
        return (
            <Button float={onCreateOrder?true : false} disabled={!onCreateOrder} transparent style={{ padding:0, margin:2, borderWidth:0, width:60, justifyContent:'center', alignItems:'center'}} onPress={() => {
                if(!trade_side_trade){ return }
                if(!onCreateOrder){ return }
                const order = MarketButtonHelpers.getOrderFromTrade(trade_side_trade);
                const market = markets.find(mk => mk.market_id == trade_side_trade.market_id);
                if(!market){ return }
                const tournament = tournaments.find(t => t.tournament_id == order.event_id);
                const event = events.find(e => e.event_id == order.event_id);
                const athlete = athletes.find(a => a.athlete_id == order.side_id);
                const team = teams.find(t => t.team_id == order.side_id);
                //const match = [].find(m => m.mark)
                let title = MarketComponentHelpers.getOrderTitle(order, market, event, tournament, undefined, athlete, team);
                if(!title){ return }
                return onCreateOrder({ ...order, title });
            }}>
                {trade_side_trade ?
                <TradeText
                    market={data.item}
                    trade={trade_side_trade}
                    width={tick_width / 2.5 / 3}
                    animate={animate}
                />
                :
                <Icons.LockClosedIcon size={12} color={Colors.text.h1} />
                }
            </Button>
        )
    }

    const  renderAthletes = (data:{item:string, index:number, tournament:TournamentProps, league?:LeagueProps}) => {
        const athlete = athletes.find(a => a.athlete_id == data.item);
        if(!athlete){ return <></> }
        let sup_markets = data.tournament.supported_markets ?? []
        const supported_markets = markets.filter(pm => sup_markets.map(sm => sm.market_id.toString()).includes(pm.market_id.toString())).sort((a,b) => parseInt(a.market_id) - parseInt(b.market_id));
        const is_live = moment().isAfter(moment(data.tournament.scheduled_datetime)) ? true : false
        const is_closed = FINALIZED_STATUSES.includes(data.tournament.status) ? true : false
        return (
            <View key={data.item.toString()} style={{ width:tick_width-10, flexDirection:'row', alignItems:'center', margin:2, padding:5, borderBottomWidth:1, borderColor:is_closed?Colors.text.error:is_live?Colors.text.success:Colors.borders.light }}>
                <View float style={{ padding:2, borderRadius:100 }}>
                    <AthleteImage
                        league={data.league}
                        athlete={athlete}
                        size={32}
                    />
                </View>
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{athlete.first_name} {athlete.last_name}</Text>
                </View>
                <View style={{ flexDirection:'row' }}>
                {supported_markets.map((sm, i) => {
                    return renderTrades({ item: sm, index:i, contest_type: 'tournament', contest_id:data.tournament.tournament_id, is_live, side_type:'athlete', side_id:data.item })
                })}
            </View>
            </View>
        )
    }

    const renderTournaments = (data:{ item:TournamentProps, index:number }) => {
        const league = leagues.find(l => l.league_id == data.item.league_id)
        return (
            <View key={data.item.tournament_id.toString()} style={{  width:embed_size.width }}>
                <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                    <View transparent style={{flex:1}}>
                        <Text theme='h1'>{data.item.tournament_name}</Text>
                        <Text theme='description' style={{ marginTop:3 }}>{moment(data.item.scheduled_datetime).format('MMM D hh:mm a')}</Text>
                    </View>
                </View>
                <View style={{ flexDirection:'row', alignItems:'center', flexWrap:'wrap', paddingLeft:10, paddingRight:10 }}>
                    {column_headers.map(c => {
                        return (
                            <View key={c.toString()} style={{ width:tick_width - 10, flexDirection:'row', alignItems:'center', paddingTop:10 }}>
                                <View style={{ flex:1 }} />
                                {data.item.supported_markets.map(sm => {
                                    const market = markets.find(md => md.market_id == sm.market_id)
                                    if(!market){ return <></> }
                                    return (
                                        <View transparent style={{ margin:2, width:60 }}>
                                            <Text theme='h2' textAlign='center'>{market.type}</Text>
                                        </View>
                                    )
                                })}
                            </View>
                        )
                    })}
                </View>
                {data.item.participant_type == 'athlete' ?
                <View style={{ flexDirection:'row', flexWrap:'wrap', padding:10, paddingTop:0 }}>
                    {data.item.participants.map((item, index) => {
                        return renderAthletes({ item, index, tournament:data.item, league })
                    })}
                </View>
                :<></>}
            </View>
        )
    }

    const renderEvents = (data: { item:EventProps, index:number }) => {
        const league = leagues.find(l => l.league_id == data.item.league_id);
        let sup_markets = data.item.supported_markets ?? []
        const supported_markets = primary_markets.filter(pm => sup_markets.map(sm => sm.market_id.toString()).includes(pm.market_id.toString())).sort((a,b) => parseInt(a.market_id) - parseInt(b.market_id));
        const is_live = moment().isAfter(moment(data.item.scheduled_datetime)) ? true : false
        const is_closed = FINALIZED_STATUSES.includes(data.item.status) ? true : false
        return (
            <View key={data.item.event_id.toString()} style={{ width:tick_width, margin:5, padding:8, borderBottomWidth:1, borderColor:is_closed?Colors.text.error:is_live?Colors.text.success:Colors.borders.light }}>
                <View key={data.item.event_id.toString()} style={{ flexDirection:'row', alignItems:'center' }}>
                    <Image
                        source={{ uri: league?.league_image }}
                        style={{ height:30, width:30 }}
                        resizeMode='cover'
                    />
                    <View style={{ flex:1, marginLeft:10, marginRight:10 }}>
                        <Text theme='h2'>{data.item.event_title}</Text>
                        <Text theme='description' style={{ marginTop:3 }}>{data.item.time_detail == 'scheduled'?`${moment(data.item.scheduled_datetime).format('MMM DD hh:mm a')}`:data.item.time_detail}</Text>
                    </View>
                    <View style={{ flexDirection:'row' }}>
                        {supported_markets.map((sm, i) => {
                            return renderTrades({ item: sm, index:i, contest_type: 'team', contest_id:data.item.event_id, is_live })
                        })}
                    </View>
                </View>
                {data.item.last_play ?
                <Text theme='description' style={{ marginTop:3 }}>{data.item.last_play}</Text>
                :<></>}
            </View>
            
        )
    }

    return (
        <View style={{ flex:1, flexDirection:'row' }} >
            <View style={{ flex:1 }} onLayout={(ev) => {
                const { width, height } = ev.nativeEvent.layout;
                setEmbedSize({ width, height });
            }}>
                {loading ?
                <ActivityIndicator style={{ margin:20, alignSelf:'center' }} size='large' color={Colors.text.h1} />
                :<></>}
                <View style={{ flexDirection:'row' }}>
                    {sorted_tournaments.map((e,i) => {
                        return renderTournaments({ item:e, index:i })
                    })}
                </View>
                <View style={{ flexDirection:'row', flexWrap:'wrap', overflow:'scroll' }}>
                    {sorted_events.map((e,i) => {
                        return renderEvents({ item:e, index:i })
                    })}
                </View>
            </View>
            {distinct_id ?
            <SocketManager
                distinct_id={distinct_id}
                subscribed_events={['V1_UPDATE_LATEST_TRADE','V1_UPDATE_EVENT', 'V1_UPDATE_ORDERS']}
                onConnect={() => {
                    if(needs_reload){ getData() }
                }}
                onDisconnect={() => setNeedsReload(true)}
                onSocketEvent={(ev) => {
                    switch(ev.type){
                        case 'V1_UPDATE_LATEST_TRADE':
                            return setSocketTrade(ev.data)
                        case 'V1_UPDATE_EVENT':
                            return setSocketEvent(ev.data)
                        case 'V1_UPDATE_ORDERS':
                            if(!onOrders){ return }
                            return onOrders(ev.data)
                        default:return
                    }
                }}
            />
            :<></>}
        </View>
    )
}

export default Ticker